import { backButton } from '@telegram-apps/sdk-react';
import debug from 'debug';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { match } from 'ts-pattern';
import { ROUTES } from '~/constants';
import { isTelegramApp } from '~/lib/telegram';
import { useSheetStore } from '~/store/sheet-store';

const log = debug('app:back-button');

const ROOT_ROUTES = [ROUTES.STICKERS, ROUTES.ONBOARDING, ROUTES.EARN, ROUTES.MY_COLLECTIONS];

function isRootRoute(pathname: string) {
  return ROOT_ROUTES.includes(pathname);
}

type BackButtonBehavior = 'BACK_BUTTON' | 'CLOSE_APP' | 'CLOSE_SHEET';

export const useTelegramBackButton = () => {
  const router = useRouter();
  const pathname = usePathname();
  const hasActiveSheets = useSheetStore((st) => st.hasActiveSheets());

  useEffect(() => {
    if (!isTelegramApp(window)) {
      return;
    }

    if (!backButton.isSupported()) {
      return;
    }

    // Mount back button if not already mounted
    if (!backButton.isMounted()) {
      backButton.mount();
    }

    // Determine behavior based on priority: CLOSE_SHEET > BACK_BUTTON > CLOSE_APP
    const behavior: BackButtonBehavior = match('')
      .when(
        () => hasActiveSheets,
        () => 'CLOSE_SHEET' as const,
      )
      .when(
        () => isRootRoute(pathname),
        () => 'CLOSE_APP' as const,
      )
      .otherwise(() => 'BACK_BUTTON');

    log('updating', behavior, pathname);

    const handleBackClick = () => {
      match(behavior)
        .with('CLOSE_SHEET', () => {
          // Close the top sheet in the stack
          const topSheet = useSheetStore.getState().getTopSheet();
          if (topSheet) {
            topSheet.onClose();
            useSheetStore.getState().popSheet();
          }
        })
        .with('BACK_BUTTON', () => {
          // Prevent going back from forge result screen
          if (pathname.startsWith('/workshop/forge/request/')) {
            router.replace(ROUTES.WORKSHOP_FORGE);
            return;
          }

          // Check if there's history to go back to
          if (window.history.length > 1) {
            router.back();
          } else {
            // Fallback to home if no history
            router.push(ROUTES.STICKERS);
          }
        })
        .with('CLOSE_APP', () => {
          // Do nothing - button should be hidden
        })
        .exhaustive();
    };

    const subscriptions: { unsubscribe: () => void }[] = [];

    match(behavior)
      .with('CLOSE_APP', () => {
        backButton.hide();
      })
      .with('BACK_BUTTON', () => {
        backButton.show();
        backButton.onClick(handleBackClick);
        subscriptions.push({
          unsubscribe: () => {
            backButton.offClick(handleBackClick);
          },
        });
      })
      .with('CLOSE_SHEET', () => {
        backButton.show();
        backButton.onClick(handleBackClick);
        subscriptions.push({
          unsubscribe: () => {
            backButton.offClick(handleBackClick);
          },
        });
      })
      .exhaustive();

    return () => {
      subscriptions.forEach((subscription) => {
        subscription.unsubscribe();
      });
    };
  }, [pathname, router, hasActiveSheets]);
};
